'use client';

import { useRecurly } from '@recurly/react-recurly';
import { PayPalInstance } from '@recurly/recurly-js/lib/paypal';
import Image from 'next/image';
import { useEffect, useState, useCallback, useRef, JSX } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { PayPalService } from './PayPalService';
import { getPaypalBraintreeClientAuthorization } from './actions';

export type PayPalCheckoutProps = {
    onSuccess: (data: PayPalSuccessData) => void;
    onError: (error: { code: string; message: string; cause?: unknown }) => void;
    disabled?: boolean;
    className?: string;
};

export type PayPalSuccessData = {
    tokenId: string;
};

export type PayPalToken = {
    id: string;
    email: string;
    payer_id: string;
};

export type PayPalError = {
    code: string;
    message: string;
    cause?: unknown;
};

const logger = LoggerFactory.create('PayPalCheckout');

export default function PayPalCheckout({
    onSuccess,
    onError,
    disabled = false,
    className = '',
}: PayPalCheckoutProps): JSX.Element {
    const paypalConfig = PayPalService.getConfig();
    const recurly = useRecurly();
    const [paypalInstance, setPaypalInstance] = useState<PayPalInstance | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [clientAuthorization, setClientAuthorization] = useState<string | undefined>(undefined);

    // Use refs to always have access to the latest callbacks
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    // Update refs when callbacks change
    useEffect(() => {
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [onSuccess, onError]);

    const initializePayPal = useCallback(() => {
        if (isInitialized) return;

        try {
            const paypal = PayPalService.createPayPalInstance(recurly, clientAuthorization);

            paypal.on('error', (err: PayPalError) => {
                setIsLoading(false);
                onErrorRef.current({
                    code: err.code || 'PAYPAL_ERROR',
                    message: err.message || 'An error occurred with PayPal',
                    cause: err.cause,
                });
            });

            paypal.on('token', (token: PayPalToken) => {
                setIsLoading(false);

                const successData: PayPalSuccessData = {
                    tokenId: token.id,
                };

                if (paypalConfig.useMockPayPal) {
                    logger.info('Mock PayPal: Success - Token generated');
                    return;
                }

                onSuccessRef.current(successData);
            });

            paypal.on('cancel', () => {
                setIsLoading(false);
                onErrorRef.current({
                    code: 'CANCEL_ERROR',
                    message: 'PayPal payment was cancelled',
                });
            });

            setPaypalInstance(paypal);
            setIsInitialized(true);
        } catch (error) {
            onErrorRef.current({
                code: 'INIT_ERROR',
                message: 'Failed to initialize PayPal',
            });
        }
    }, [isInitialized, paypalConfig.useMockPayPal, recurly, clientAuthorization]);

    // Fetch client authorization when component mounts (only if not using mock)
    useEffect(() => {
        if (!paypalConfig.useMockPayPal && !clientAuthorization) {
            getPaypalBraintreeClientAuthorization()
                .then(setClientAuthorization)
                .catch(() => {
                    onErrorRef.current({
                        code: 'AUTH_ERROR',
                        message: 'Failed to initialize PayPal. Please try again.',
                    });
                });
        }
    }, [paypalConfig.useMockPayPal, clientAuthorization]);

    useEffect(() => {
        if (paypalConfig.useMockPayPal || (recurly && clientAuthorization && !isInitialized)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            initializePayPal();
        }
    }, [initializePayPal, paypalConfig.useMockPayPal, recurly, clientAuthorization, isInitialized]);

    const handlePayPalClick = () => {
        if (!paypalInstance || isLoading || disabled) return;

        setIsLoading(true);

        try {
            paypalInstance.start();
        } catch (error) {
            setIsLoading(false);

            const errorMessage =
                'There has been an error processing your payment. Retry your request or try a different payment method. Contact customer support at support@storyblocks.com for more help.';

            onErrorRef.current({
                code: 'START_ERROR',
                message: errorMessage,
            });
        }
    };

    return (
        <button
            type="button"
            onClick={handlePayPalClick}
            disabled={disabled || isLoading || !isInitialized}
            className={`w-full flex items-center justify-center py-1 px-4 border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            {isLoading ? (
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-gray-600">Loading...</span>
                </div>
            ) : (
                <div className="flex items-center gap-0.5">
                    <Image
                        src="/assets/images/paypal-logo.png"
                        alt="PayPal"
                        width={28}
                        height={28}
                        className="object-contain -mt-1"
                    />
                    <span
                        className="font-bold italic tracking-tight text-lg"
                        style={{
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            letterSpacing: '-0.5px',
                            fontSize: '1.125rem',
                            lineHeight: '1.75rem',
                            cursor: 'pointer',
                        }}
                    >
                        <span style={{ color: '#003087' }}>Pay</span>
                        <span style={{ color: '#009CDE' }}>Pal</span>
                    </span>
                </div>
            )}
        </button>
    );
}
